import React, { useMemo, useState } from "react";
import StatsBox from "../StatsPage/StatsBox.js";
import ResultInfoText from "../ResultsTable/ResultInfoText.js";
import KingdomIcon from "../ReactionTable/kingdomIcon.js";
import MoleculesDiv from "../MoleculeTable/MoleculesDiv.js";
import BasicRecordTable from "./BasicRecordTable.js";
import EnzymeDisplayBox from "../EnzymeDisplayBox.js";
import MechanismViewer from "../MechanismView/mechanismViewer.js";
import SpeciesList from "./SpeciesList.js";
import getDataByEnzymesFromSearchResults from "../../api/transformSearchResultsToEnzymes.js";
import { getUniqueMoleculesFromEnzyme } from "../../api/HelperFunctions.js";
import { summariseTaxonomy } from "../../functions/taxonomySummary.js";
import { kingdomLink } from "../../functions/taxonomyLinks.js";
import translateTerpeneType from "../../api/typeTranslator.js";

// Above this many distinct mechanisms the mechanism network is put behind a
// button instead of being drawn on load. /getMechNet itself is fast (<1s even
// for the largest kingdom); what does not scale is the viewer laying every node
// out with dagre on the main thread. Calibrated against release 2026-08 —
// mechanisms map to roughly 2.2x as many deduplicated molecule nodes:
//   busiest species  Arabidopsis thaliana   79 mechanisms / 213 nodes
//   Plantae (Gymnosperm)                   100 mechanisms / 216 nodes
//   Fungi 169 · Bacteria 252 · Plantae (Angiosperm) 276 mechanisms / 602 nodes
// So 100 lets every species page and every kingdom but the big three render
// straight away, and gates exactly the graphs that are too dense to read anyway.
const MECHANISM_AUTOLOAD_LIMIT = 100;

// Shared body of both taxonomy pages: `mode` is "species" or "kingdom".
// Everything below is derived from the one /basicsearch response in `rows`
// (see api/taxonomySearch.js), so the two pages differ only in their basic-info
// block and in the kingdom-only species list.
function TaxonomyRecord({ mode, rows, setMechBody, navigateToRecord }) {
  const [showMechanisms, setShowMechanisms] = useState(false);

  // getDataByEnzymesFromSearchResults sorts its input in place, so hand it a
  // copy — `rows` is state owned by TaxonomyPage.
  const enzymes = useMemo(() => getDataByEnzymesFromSearchResults([...rows]), [rows]);
  const summary = useMemo(() => summariseTaxonomy(enzymes), [enzymes]);
  // Products only: the substrate side of a species is nearly always just the
  // handful of prenyl diphosphates, so it carries no information here. Same
  // helper (and so the same "Found in: N reactions" count and ordering) as the
  // "Products from this molecule" box on a molecule record.
  const products = useMemo(() => getUniqueMoleculesFromEnzyme(enzymes).products, [enzymes]);

  const isSpecies = mode === "species";
  // The label as MARTS stores it, taken from the data rather than from the URL
  // segment. Everything downstream that puts it back into a query — the
  // mechanism viewer via /getMechNet, which interpolates its species/kingdoms
  // filters into the SQL string — gets this one, never the raw param.
  const dbValue = isSpecies ? enzymes[0]?.species : enzymes[0]?.kingdom;
  const kingdom = isSpecies ? enzymes[0]?.kingdom : dbValue;

  // "sesquiterpene (34) · monoterpene (12)". Object keys come back as strings,
  // so a NULL type/class arrives as "null" — label those rather than printing it.
  const countList = (counts, translate) =>
    counts.length === 0
      ? "None"
      : counts
          .map(([key, count]) => {
            const missing = key === "null" || key === "undefined" || key === "";
            const label = missing ? "Unspecified" : translate ? translate(key) : key;
            return `${label} (${count})`;
          })
          .join(" · ");

  const mechanismSearchData = useMemo(
    () => (isSpecies ? { species: [dbValue] } : { kingdoms: [dbValue] }),
    [isSpecies, dbValue]
  );

  const basicInfoRows = isSpecies
    ? ["Kingdom", "Enzymes", "Reactions", "Reaction types", "Reaction classes", "Mechanisms", "Products"]
    : ["Species", "Enzymes", "Reactions", "Reaction types", "Reaction classes", "Mechanisms", "Products"];

  const basicInfoRecords = [
    isSpecies ? (
      <>
        <ResultInfoText
          text={kingdom}
          row={1}
          navigationLink={kingdomLink(kingdom)}
          buttonClass="enzyme-record-link"
          tooltipText={`See everything MARTS holds for ${kingdom}`}
        />
        <KingdomIcon
          row={-30}
          isQuickSearch={true}
          interactiveSearch={() => {}}
          backgroundColor="rgb(255,255,255, 0)"
          kingdomVal={kingdom}
          speciesVal={dbValue}
        />
      </>
    ) : (
      summary.speciesCounts.length
    ),
    summary.enzymeCount,
    summary.reactionCount,
    countList(summary.typeCounts, translateTerpeneType),
    countList(summary.classCounts, (cls) => `Class ${cls}`),
    summary.mechanismCount,
    products.length,
  ];

  return (
    <>
      <StatsBox
        title={
          <>
            <ResultInfoText
              text={dbValue}
              row={1}
              fontSize="25px"
              buttonClass="plane-text-button-no-background"
            />
            {!isSpecies && (
              <KingdomIcon
                row={-31}
                isQuickSearch={true}
                interactiveSearch={() => {}}
                backgroundColor="rgb(255,255,255, 0)"
                kingdomVal={dbValue}
                speciesVal={"None"}
              />
            )}
          </>
        }
      >
        <BasicRecordTable rows={basicInfoRows} records={basicInfoRecords} />
      </StatsBox>

      {!isSpecies && (
        <StatsBox
          title={
            <ResultInfoText
              text={`Species in ${dbValue} (${summary.speciesCounts.length})`}
              row={1}
              buttonClass="plane-text-button-no-background"
              tooltipText={`Every species of ${dbValue} with at least one reaction in MARTS, ordered by how many enzymes it contributes.`}
            />
          }
        >
          <SpeciesList speciesCounts={summary.speciesCounts} />
        </StatsBox>
      )}

      {products.length > 0 && (
        <StatsBox
          title={
            <ResultInfoText
              text={`Products of ${dbValue} (${products.length})`}
              row={1}
              buttonClass="plane-text-button-no-background"
              tooltipText={`Every molecule produced by an enzyme of ${dbValue}, ordered by the number of MARTS reactions producing it.`}
            />
          }
        >
          <MoleculesDiv molecules={products} startingDisplay={15} displayStep={15} />
        </StatsBox>
      )}

      {summary.mechanismCount > 0 && (
        <StatsBox title={`Mechanisms (${summary.mechanismCount})`}>
          {showMechanisms || summary.mechanismCount <= MECHANISM_AUTOLOAD_LIMIT ? (
            <MechanismViewer
              searchData={mechanismSearchData}
              orientation={summary.mechanismCount > 5 ? "TD" : "LR"}
              height="1000px"
            />
          ) : (
            <>
              <div>
                {dbValue} has {summary.mechanismCount} mechanisms in MARTS. Drawing them as one
                network takes a while and produces a large graph, so it is not loaded
                automatically.
              </div>
              <button className="basic-button" onClick={() => setShowMechanisms(true)}>
                Show mechanism network
              </button>
            </>
          )}
        </StatsBox>
      )}

      <StatsBox title={`Enzymes and reactions (${summary.enzymeCount})`}>
        <EnzymeDisplayBox
          reactionsDataByEnzymes={enzymes}
          setMechBody={setMechBody}
          navigateToRecord={navigateToRecord}
          startingDisplay={10}
          displayStep={10}
        />
      </StatsBox>
    </>
  );
}

export default TaxonomyRecord;
